/**
 * Helpers for the Project Files page's link-aware behaviour: explaining a refused delete, guarding
 * folder delete, and deciding whether "move to task" should link instead.
 *
 * Pure functions in their own module so they can be tested without mounting the page, and so the
 * page keeps one import rather than growing four more exports.
 */

const MAX_NAMED_TASKS = 3;

/**
 * Blocked file ids from the mutation, resolved against the rows this page already holds. The
 * mutation deliberately returns ids only -- every project file is already loaded here with its name
 * and its linkedTasks, so sending those back through the payload would duplicate them.
 *
 * An id that does not resolve still produces an entry. A refusal the user cannot explain is the
 * exact failure this message exists to prevent, so a missing row degrades to "this file, tasks
 * unknown" rather than to silence.
 */
export function resolveBlockedFiles(blockedFileIds, fileEdges) {
	const byId = new Map((fileEdges || []).filter(edge => edge && edge.node).map(edge => [edge.node.id, edge.node]));
	return (blockedFileIds || []).map(id => {
		const node = byId.get(id);
		return {
			id,
			name: node ? node.name : id,
			linkedTasks: node && node.linkedTasks ? node.linkedTasks : [],
		};
	});
}

/**
 * Why a delete was refused. Project Files shows no other indication of what holds a file, so
 * without this the user is told "no" with nothing to act on. Truncated because a bulk delete of
 * several files each linked to several tasks would otherwise produce an unreadable wall.
 */
export function blockedDeleteMessage(blockedFiles) {
	return (blockedFiles || [])
		.map(file => {
			const tasks = file.linkedTasks || [];
			const named = tasks.slice(0, MAX_NAMED_TASKS).map(task => `T${task.companyTaskId} ${task.name}`);
			const remainder = tasks.length - named.length;
			const suffix = remainder > 0 ? `, +${remainder}` : '';
			return `${file.name}: ${named.join(', ')}${suffix}`;
		})
		.join('\n');
}

/**
 * A folder cannot be deleted while a file inside it is linked to a task -- deleting it would take
 * the file with it and defeat the guard on the file itself.
 */
export function folderHoldsLinkedFile(folder, fileEdges) {
	return (fileEdges || []).some(
		edge =>
			edge &&
			edge.node &&
			edge.node.folder &&
			edge.node.folder.id === folder.node.id &&
			(edge.node.linkedTasks || []).length > 0
	);
}

/**
 * Ids of the files the project itself owns, i.e. everything the Project_files connection returned.
 *
 * This exists because the GraphQL File type exposes no projectId field -- it resolves `project` as
 * an object but never surfaces the raw id -- so membership of that connection is the available
 * evidence that a file is project-owned.
 */
export function projectFileIdSet(fileEdges) {
	return new Set((fileEdges || []).filter(edge => edge && edge.node).map(edge => edge.node.id));
}

/**
 * Whether "move file to task" should link rather than move.
 *
 * A project-owned file keeps its project relationship, so linking is both possible and correct --
 * and moving it would null project_id, which looks identical on this page but silently drops the
 * file out of the picker and out of the delete guard's protection.
 *
 * A file uploaded straight to a task has no project relationship to keep, so it still moves. Google
 * Drive files take their own path entirely.
 */
/**
 * Splits a bulk "move to task" selection into the files that should be LINKED (project-owned) and
 * the files that should still be MOVED (everything else).
 *
 * Exported and pure so the rule is assertable on its own: the selection holds ids rather than file
 * objects, so this is the only place the ownership decision is made for the bulk path, and getting
 * it wrong silently reverts project-owned files to being moved -- which strips their project
 * relationship and reopens the bypass around the delete guard.
 */
export function partitionMoveTargets(fileIds, projectFileIds) {
	const toLink = [];
	const toMove = [];
	(fileIds || []).forEach(id => {
		if (projectFileIds && projectFileIds.has(id)) {
			toLink.push(id);
		} else {
			toMove.push(id);
		}
	});
	return {toLink, toMove};
}

export function moveTargetIsLink(file, projectFileIds) {
	if (!file || !file.node) return false;
	if (file.node.link) return false;
	if (file.node.task) return false;
	return !!projectFileIds && projectFileIds.has(file.node.id);
}
